import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Product } from './ProductCard';

export interface CartItem {
  product: Product;
  qty: number;
}

interface CartCtx {
  items: CartItem[];
  count: number;
  subtotal: number;
  add: (p: Product, qty?: number) => void;
  remove: (id: string | number) => void;
  setQty: (id: string | number, qty: number) => void;
  clear: () => void;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export interface CartProviderProps {
  children: ReactNode;
  /**
   * Optional user id used to scope the persisted cart. When present, cart
   * items are stored under `kss.cart.v1:<userId>`; when absent (logged-out
   * guest), items live under `kss.cart.v1:guest`. Switching `userId` swaps
   * the active cart so a sign-in/sign-out doesn't leak items between users.
   */
  userId?: string | null;
}

const Ctx = createContext<CartCtx | null>(null);
const STORAGE_PREFIX = 'kss.cart.v1';

function storageKey(userId?: string | null) {
  return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readCart(userId?: string | null): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as CartItem[];
  } catch {}
  return [];
}

export function CartProvider({ children, userId }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>(() => readCart(userId));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lastUserId = useRef<string | null | undefined>(userId);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Swap carts when the user switches (login or logout).
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    setItems(readCart(userId));
  }, [userId]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(items));
    } catch {}
  }, [items, userId]);

  const add = useCallback((product: Product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { product, qty }];
    });
    // Auto-open the side cart drawer like a POS order panel.
    setDrawerOpen(true);
  }, []);

  const remove = useCallback((id: string | number) => {
    setItems((prev) => prev.filter((i) => i.product.id !== id));
  }, []);

  const setQty = useCallback((id: string | number, qty: number) => {
    /* Clamp to minimum 1 so the qty stepper's "–" button at qty=1
       leaves the item in the cart instead of silently deleting it.
       The user has to press the Trash button to remove an item —
       that's the discoverable, intentional path. Bug ref: AI Agent
       2026-05-26 — "add cart quantity describe then product remove
       by default; it should be only minimum quantity should 1". */
    const next = Math.max(1, Math.floor(Number.isFinite(qty) ? qty : 1));
    setItems((prev) =>
      prev.map((i) => (i.product.id === id ? { ...i, qty: next } : i)),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<CartCtx>(() => {
    const count = items.reduce((a, i) => a + i.qty, 0);
    const subtotal = items.reduce((a, i) => a + i.qty * i.product.price, 0);
    return {
      items,
      count,
      subtotal,
      add,
      remove,
      setQty,
      clear,
      drawerOpen,
      openDrawer,
      closeDrawer,
    };
  }, [items, add, remove, setQty, clear, drawerOpen, openDrawer, closeDrawer]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCart must be used within <CartProvider>');
  return v;
}
